import { addToast } from "@heroui/react";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";
import { parseUnits, encodeFunctionData } from "viem";
import { rainbowKitConfig } from "./config";
import { writeContractApi } from "../api/methods-marketplace";

const ERC721_ABI = [
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "tokenId", type: "uint256" }
    ],
    outputs: []
  }
];

const ApproveContract = async (
  nftAddress,
  transferProxyAddress,
  tokenId,
  isCustodialWallet = false,
  publicClient = null
) => {
  try {
    const tokenIdParsed = parseUnits(tokenId.toString(), 0);

    if (isCustodialWallet) {
      if (!publicClient) {
        throw new Error("publicClient is required for custodial wallet");
      }

      const approveCallData = encodeFunctionData({
        abi: ERC721_ABI,
        functionName: "approve",
        args: [transferProxyAddress, tokenIdParsed]
      });

      const approvalTx = await writeContractApi({
        address: nftAddress,
        encoded_data: approveCallData
      });

      if (!approvalTx?.data?.tx_hash) {
        throw new Error("Approval transaction hash not received");
      }

      const approvalReceipt = await publicClient.waitForTransactionReceipt({
        hash: approvalTx.data.tx_hash
      });

      if (approvalReceipt.status !== "success") {
        addToast({
          title: "Approval Failed",
          description: "NFT approval transaction failed on blockchain.",
          color: "danger",
          variant: "bordered"
        });
        throw new Error("Approval transaction failed on blockchain");
      }

      addToast({
        title: "Approval Successful",
        description: "You have successfully approved the NFT for listing.",
        color: "success",
        variant: "bordered"
      });

      return {
        success: true,
        txHash: approvalTx.data.tx_hash,
        receipt: approvalReceipt
      };
    } else {
      const txHash = await writeContract(rainbowKitConfig, {
        address: nftAddress,
        abi: ERC721_ABI,
        functionName: "approve",
        args: [transferProxyAddress, tokenIdParsed]
      });

      const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
        hash: txHash
      });

      addToast({
        title: "Approval Successful",
        description: "You have successfully approved the NFT for listing.",
        color: "success",
        variant: "bordered"
      });

      return { success: true, txHash, receipt };
    }
  } catch (err) {
    const userRejected = err?.code === 4001 || err?.info?.error?.code === 4001;

    if (userRejected) {
      addToast({
        title: "Approval Rejected",
        description: "You rejected the approval transaction from your wallet.",
        color: "warning",
        variant: "bordered"
      });
    } else {
      console.error("Approval Error:", err);
      addToast({
        title: "Approval Failed",
        description:
          err?.response?.data?.message ||
          err?.message ||
          "An error occurred while trying to approve the listing.",
        color: "danger",
        variant: "bordered"
      });
    }

    throw err;
  }
};

export default ApproveContract;

